// Offer-System — wie ein User-Sign-up je nach Marketing-Quelle anders verhält.
//
// Match-Quelle: `users/{uid}.attribution.source` (gespeichert beim Sign-up
// aus URL-Param `?source=` / `?offer=` oder `ls_offer`-Cookie via
// `apps/auth/src/lib/attribution.ts`).
//
// Zwei Wirkebenen pro Offer:
//   1. SIGN-UP-EFFECTS — apply-once beim UserDoc-Create (Initial-Credits,
//      Trial-Phase, Auto-Plan). Werden im Auth-Sign-up-Handler appliziert.
//   2. RUNTIME-OVERRIDES — bei jedem `/api/me`-Read frisch resolved und
//      ans Frontend mitgegeben. Frontend rendert Pricing-Variant + Slot-
//      Overrides. KEIN persistenter UI-State pro User — wenn ein Offer
//      `active: false` gesetzt wird, ändert sich das Verhalten ab dem
//      nächsten /api/me-Call.
//
// Bewusster Trade-off: Catalog lebt im Code, nicht in einer Datenbank.
// Janik fügt einen Offer hinzu = Edit dieser Datei → Commit → Deploy.
// Sandbox unter `/sandbox/?offer=<id>` rendert die Offers vor Live-Gang.
// → Nicht skalierbar auf 100+ Offers, aber für Solopreneur-Realität ideal.

import type { Lang } from './lang';

// ---------------------------------------------------------------------------
// Slot-System — UI-Stellen die per Offer überschrieben werden können
// ---------------------------------------------------------------------------

/**
 * Slot-IDs die das Live-Frontend kennt + per `<OfferSlot id="..." />` rendert.
 *
 * **Bewusst minimal gehalten** (Janik 2026-04-29): das Offer-System ist
 * primär ein Paywall-Variant- + Sign-up-Effects-Mechanismus. Wir streuen
 * KEINE custom UI-Components per Offer durchs Produkt — Beta-User sehen
 * das gleiche Dashboard wie alle, sie bekommen nur eine andere Paywall +
 * andere Initial-Credits. Slot-IDs hier nur erweitern wenn wirklich klar
 * ist dass ein UI-Element offer-aware werden muss.
 */
export type SlotId = 'pricing.intro'; // Banner-Text über den Plans im Pricing-Modal

export type SlotKind =
  // Standard-Layout, aber Text aus i18n-Key.
  { kind: 'text'; textKey: string };

export type SlotVariant = SlotKind;

// ---------------------------------------------------------------------------
// Pricing-Variants — welche Plans im Pricing-Modal angezeigt werden
// ---------------------------------------------------------------------------

export type PlanId = 'basic' | 'starter' | 'growth';

export type PricingVariantId = 'standard' | 'trial-3d-50credits' | 'beta-only-growth' | string;

/**
 * Layout-Selector im OnboardingGate. Default = 'default' (3-plan grid).
 * 'split-testimonials' = single hero plan card links + Testimonials-Spalte
 * rechts (LEA-261, 2026-05-11). 'trust-redesign' = 3-Plan-Grid mit Gründer-
 * Nachricht, Live-Demo-Video, Trial-Timeline, Testimonials-Reihe und reframed
 * Trust-Bar (LEA-319, 2026-06-11). Beta nutzt weiterhin seine eigene Layout-
 * Verzweigung (single plan + forcePlanHero ohne layout-Flag).
 * 'prism' = eigener mehrstufiger Aktivierungs-Funnel (Zielgruppe → Website →
 * KI-Analyse → 3 Fragen → Suche → Aha-Ergebnis → Paywall → Inline-Checkout),
 * gerendert vom separaten `features/prism/`-Modul (PrismShell). Der Gate
 * verzweigt bei diesem Layout ganz früh und rendert PrismShell statt der
 * Plan-Karten (Prism 2026-07-01).
 * 'r19' = Signup-/Paywall-Redesign Revision 19 (Janik-Freigabe 11.09.2026):
 * zweispaltig — links Angebots-Headline („Finde bis zu 50 kostenlose Leads"),
 * Bewertung und Produkteinblick, rechts die Paketwahl mit vorgezogenem
 * Test-Block (50 Credits · 3 Tage · 0 € heute) vor der Paketauswahl.
 * REIN PRÄSENTATIV: identische Plans, Preise, Trial-Dauer und Checkout wie
 * 'trust-redesign'.
 * UMSCHALTUNG UND ROLLBACK LAUFEN NICHT ÜBER DIESE DATEI, sondern über die
 * Env `PAYWALL_R19_ENABLED` auf der API (apps/api/src/lib/offerService.ts,
 * withR19Presentation). Das Offer '50credits-trial' unten MUSS auf
 * `variant: '50credits-trust'` stehen bleiben — der Schalter hängt genau an
 * diesem Wert. Wer ihn hier auf '50credits-r19' setzt, verdrahtet die neue
 * Optik fest UND legt den Sofort-Rückweg lahm.
 */
export type PricingLayout = 'default' | 'split-testimonials' | 'trust-redesign' | 'prism' | 'r19';

export type PricingVariantConfig = {
  id: PricingVariantId;
  /** Lesbares Label (Sandbox-Sidebar, Admin-Anzeigen). */
  label: string;
  /** 1-Satz Beschreibung was diese Paywall macht. */
  description: string;
  /** Welche Plans werden angezeigt — andere ausgeblendet. */
  showPlans: PlanId[];
  /** Soll der "+25 Test-Leads"-Hinweis (Trial-Credits) gezeigt werden? */
  showTrialCreditsHint: boolean;
  /** i18n-Key für die Modal-Headline (Live-Frontend resolved). */
  headlineKey?: string;
  /** i18n-Key für den Modal-Sub-Text. */
  subKey?: string;
  /** Bei Single-Plan-Variants: Plan-Card automatisch im Hero-Look (Gradient + Glow). */
  forcePlanHero?: boolean;
  /** Layout-Variante im OnboardingGate. 'split-testimonials' = single
   *  Plan-Card + Testimonials-Spalte rechts (LEA-261). Ungesetzt = default. */
  layout?: PricingLayout;
};

/**
 * Catalog aller Pricing-Variants. Live-Frontend liest `me.offer.pricing.variant`,
 * mappt es zu einem dieser Configs und rendert die Plans entsprechend.
 */
export const PRICING_VARIANTS: Record<PricingVariantId, PricingVariantConfig> = {
  standard: {
    id: 'standard',
    label: 'Standard (3 Plans · Standard-Trial)',
    description: 'Live-Stand: drei Plans zur Auswahl, Standard-Trial.',
    showPlans: ['basic', 'starter', 'growth'],
    showTrialCreditsHint: true,
    // Default-Headline + subText sind im Live-Modal hardcoded ("unfairer
    // Wettbewerbsvorteil ..."). Wenn nichts hier gesetzt → Default greift.
  },
  // V1.25.23 (Janik 2026-05-03): Marketing-Push mit verlängertem Trial.
  // 50 Credits + 3 Tage. Nur Abos-Tab im Modal sichtbar (= Einmalpakete
  // werden vor Conversion versteckt). Live-Frontend rendert Variante
  // basierend auf den Specs in apps/web/public/prototype/sandbox/offers/
  // 50credits-trial/pricing.html. Shanky bringt das in cortex-web live.
  'trial-3d-50credits': {
    id: 'trial-3d-50credits',
    label: 'Trial 3 Tage · 50 Credits',
    description: 'Drei Plans mit verlängertem Trial: 3 Tage + 50 kostenlose Credits. Nur Abos-Tab.',
    showPlans: ['basic', 'starter', 'growth'],
    showTrialCreditsHint: true,
    headlineKey: 'pricing.trial50.headline',
    subKey: 'pricing.trial50.subText',
  },
  'beta-only-growth': {
    id: 'beta-only-growth',
    label: 'Beta · Growth-only mit 55 % Rabatt',
    description: 'Beta-Einladung: nur Growth-Plan, kein Trial, dauerhaft 55 % Rabatt.',
    showPlans: ['growth'],
    showTrialCreditsHint: false,
    headlineKey: 'pricing.beta.headline',
    subKey: 'pricing.beta.subText',
    forcePlanHero: true,
  },
  // LEA-261 (Janik 2026-05-11) Redesign: "50-Credits-Trial" wird auf das
  // neue Split-Testimonials-Layout umgestellt (single Plan-Hero + 4
  // Testimonials rechts). Backend-Mechanik bleibt 1:1: 3-Tage-Trial,
  // 50 Credits via Stripe-Webhook, Auto-Convert nach Trial. Der frühere
  // 3-Plan-Picker entfällt — der CTA pinned auf Starter (Janiks Entschei-
  // dung, Linear-Comment 2026-05-11: "50 Credits maps cleanest to Starter").
  // Visual-Quelle: ../50credits-trial-pricing-wall.html (Linear-Attachment).
  '50credits-trial': {
    id: '50credits-trial',
    label: '50-Credits-Trial (Single Hero · Starter · Testimonials)',
    description:
      'Single Plan-Hero (Starter) + Testimonials-Spalte. 3-Tage-Trial mit 50 Credits, auto-convert.',
    showPlans: ['starter'],
    showTrialCreditsHint: true,
    headlineKey: 'pricing.trial50.headline',
    subKey: 'pricing.trial50.subText',
    layout: 'split-testimonials',
  },
  // LEA-261 (Janik 2026-05-11): "Mai-Aktion 2026" — Basic-Plan, erster
  // Monat 49 € (statt 190 €) via Stripe-Coupon MAI74. Evergreen — keine
  // Expiry-Logik im Code (das "May campaign expires soon"-Framing lebt
  // ausschließlich als Visual-Element auf der Marketing-Landingpage).
  // Visual-Quelle: ../mai-offer-pricing-wall.html (Linear-Attachment).
  'mai-offer': {
    id: 'mai-offer',
    label: 'Mai-Aktion (Single Hero · Basic 49 € erster Monat · Testimonials)',
    description:
      'Single Plan-Hero (Basic) + Testimonials-Spalte. Erster Monat 49 € via MAI74-Coupon, danach 190 €/Monat.',
    showPlans: ['basic'],
    showTrialCreditsHint: false,
    headlineKey: 'pricing.mai.headline',
    subKey: 'pricing.mai.subText',
    layout: 'split-testimonials',
  },
  // LEA-319 (Janik 2026-06-11) Trust-Redesign: löst das Split-Testimonials-
  // Layout für die 50-Credits-Trial-Kampagne ab. Wieder ein 3-Plan-Grid
  // (Basic/Starter/Growth) — diesmal eingerahmt von einer persönlichen
  // Gründer-Nachricht, einem Live-Demo-Video (per-Locale dub-Player), einer
  // Trial-Timeline (Heute 0 € / Tag 1–3 / ab Tag 3), per-Button-CTA-Microcopy,
  // einer 4-Karten-Testimonials-Reihe und einer Benefit-gerahmten Trust-Bar +
  // Founder-Line. Backend-Mechanik bleibt 1:1 identisch zum bisherigen
  // 50credits-trial-Offer (3-Tage-Trial, 50 Credits via Stripe-Webhook,
  // auto-convert) — nur die Paywall-Optik ändert sich. Rollback = das Offer
  // unten wieder auf `variant: '50credits-trial'` zeigen lassen (one-liner).
  // Visual-Quelle: ../sandbox/pricing-trust-50credits{,-en}.html (approved
  // 2026-06-10). i18n-Keys: og.trust.* + og.title.trust.* + og.subtitle.trust.
  '50credits-trust': {
    id: '50credits-trust',
    label: '50-Credits-Trial · Trust-Redesign (3 Plans · Founder · Video · Testimonials)',
    description:
      'Trust-Redesign Forced-Gate: 3-Plan-Grid mit Gründer-Nachricht, Live-Demo-Video, Trial-Timeline, Testimonials und reframed Trust-Bar. 3-Tage-Trial mit 50 Credits, auto-convert.',
    showPlans: ['basic', 'starter', 'growth'],
    showTrialCreditsHint: true,
    headlineKey: 'pricing.trial50.headline',
    subKey: 'pricing.trial50.subText',
    layout: 'trust-redesign',
  },
  // Revision-19-Redesign der Paywall (Janik-Freigabe 11.09.2026, Design-Commit
  // 22a435e7). Gleiches Offer, gleiche Plans, gleicher Trial (3 Tage / 50
  // Credits), gleicher Checkout wie '50credits-trust' — NUR die Optik und die
  // Reihenfolge der Informationen ändern sich: der Gratis-Test (50 Credits ·
  // 3 Tage · 0 € heute) steht jetzt VOR der Paketwahl, die Credit-Erklärung
  // („1 Firma finden = 1 Credit") direkt an der Auswahl.
  // AKTIVIERUNG/ROLLBACK: Env `PAYWALL_R19_ENABLED` auf der API-App — NICHT
  // hier umhängen. 'true' schaltet die Optik ein, alles andere (oder Variable
  // weg) zurück auf 'trust-redesign'. Wirkt beim nächsten /api/auth/me, ohne
  // Deploy und ohne Auswirkung auf Konten, Abos, Credits oder Stripe.
  // Das Offer unten bleibt deshalb bewusst auf '50credits-trust'.
  '50credits-r19': {
    id: '50credits-r19',
    label: '50-Credits-Trial · Revision 19 (2 Spalten · Test vor Paketwahl)',
    description:
      'Revision-19-Paywall: links Angebot, Bewertung und Produkteinblick, rechts die Paketwahl mit vorgezogenem Test-Block (50 Credits · 3 Tage · 0 € heute). Rein präsentativ — Plans, Preise, Trial und Checkout identisch zu 50credits-trust.',
    showPlans: ['basic', 'starter', 'growth'],
    showTrialCreditsHint: true,
    headlineKey: 'pricing.trial50.headline',
    subKey: 'pricing.trial50.subText',
    layout: 'r19',
  },
  // Prism (2026-07-01): eigener Aktivierungs-Funnel-Layout. Die Plan-Auswahl,
  // Copy und der Checkout leben komplett im PrismShell-Modul — deshalb sind
  // showPlans/headlineKey/subKey hier bewusst neutral; der Gate rendert bei
  // layout:'prism' PrismShell und ignoriert diese Felder. `showTrialCreditsHint`
  // false, weil der Funnel seine eigene Wert-Dramaturgie trägt.
  prism: {
    id: 'prism',
    label: 'Prism · Aktivierungs-Funnel (Zielgruppe → Suche → Aha → Paywall)',
    description:
      'Mehrstufiger Onboarding-Funnel mit echter Suche und Inline-Checkout (49-€-Tripwire). Eigenes features/prism-Modul.',
    showPlans: ['starter'],
    showTrialCreditsHint: false,
    layout: 'prism',
  },
};

export function resolvePricingVariant(id: PricingVariantId): PricingVariantConfig {
  return PRICING_VARIANTS[id] ?? PRICING_VARIANTS.standard;
}

// ---------------------------------------------------------------------------
// OfferConfig — die ganze Wirkungs-Konfiguration eines Offers
// ---------------------------------------------------------------------------

export type OfferConfig = {
  /** Match-Wert gegen `attribution.source`. Case-insensitive. */
  id: string;
  /** Off-Toggle ohne Code-Löschung. `false` = Resolver returnt null. */
  active: boolean;
  /** Optionale Beschreibung für Janik / Doku — taucht nicht im UI auf. */
  description?: string;

  /** Sign-up-Effects — apply-once beim UserDoc-Create. */
  signup: {
    /**
     * Initial-Credits beim Sign-up.
     * - Number → exakter Wert (z.B. 0 für Beta = keine Test-Credits)
     * - 'inherit' → Default des trialPlans (heutiges Verhalten)
     */
    initialCredits: number | 'inherit';
    /** Trial-Tage-Override. Default = nicht gesetzt → 3 Tage. */
    trialDaysOverride?: number;
    /** User startet automatisch in Plan, ohne Plan-Pflicht-Modal. */
    autoTrialPlan?: PlanId;
    /**
     * LEA-453: Credits die beim Signup DIREKT ins Wallet wandern (ohne Karte).
     * Gebucht über das creditEvents-Ledger via
     * apps/api/src/services/offerSignupGrant.ts — NIEMALS direkt auf
     * creditsRemaining schreiben (siehe credits.ts:17-18).
     *
     * Bewusst NICHT `initialCredits`: das ist der Stripe-Trial-Grant, den
     * stripeWebhookHandler.ts:1764 beim ersten Abo ausschüttet — ein Wert > 0
     * dort würde einem direct2-User bei der Conversion ein ZWEITES Mal 50
     * Credits geben (ohne idempotencyKey) und im PricingModal die Trial-Copy
     * einschalten (PricingModal.jsx:129).
     *
     * undefined = kein Signup-Grant = heutiges Verhalten aller anderen Offers.
     */
    walletCredits?: number;
    /**
     * LEA-453: `false` = dieses Offer hat KEINE lokale Trial-Uhr. Das Wallet
     * ist der Zähler, nicht der Kalender. Konsequenzen:
     *   - expireTrial() lässt accountStatus auf 'trial' stehen
     *   - die kalender-basierten Trial-Mails ('trial-ending-soon',
     *     'trial-not-converted') werden unterdrückt, weil ihre Copy schlicht
     *     falsch wäre ("deine Testphase endet in 4 Stunden" — tut sie nicht)
     *
     * undefined = true = heutiges Verhalten aller anderen Offers.
     */
    localTrial?: boolean;
  };

  /** Pricing-Modal-Verhalten. */
  pricing: {
    /** Welche Pricing-Variant wird im Modal angezeigt. */
    variant: PricingVariantId;
    /** Discount-Display (Backend wendet Discount via Stripe-Coupon an). */
    discountPercent?: number;
    /** Stripe-Coupon-ID die bei Checkout-Session-Create mitgeschickt wird. */
    stripeCouponId?: string;
    /** Wenn true → Pricing-Modal wird gar nicht aufgerufen. */
    skip: boolean;
    /**
     * LEA-453: Bei einem 402 `insufficient_credits` öffnet die SPA das
     * Pricing-Modal selbst, statt nur zu toasten (App.jsx lauscht auf das
     * `cortex:out-of-credits`-Event aus lib/api.jsx).
     *
     * undefined = nur Toast = heutiges Verhalten aller anderen Offers.
     */
    openOnZeroCredits?: boolean;
  };

  /** UI-Slot-Overrides — Live-Frontend rendert. */
  slots: Partial<Record<SlotId, SlotVariant>>;

  /** Optionale Sprach-Einschränkung — Offer ist nur für diese Sprachen aktiv. */
  supportedLangs?: Lang[];
};

// ---------------------------------------------------------------------------
// OFFERS — der Catalog
// ---------------------------------------------------------------------------

/**
 * Initial-Catalog. Aktuell nur `beta`. Weitere Offers (partner-empfehlung,
 * linkedin-promo, etc.) kommen on-demand.
 *
 * Janik fügt neuen Offer hinzu:
 *   1. Eintrag hier ergänzen.
 *   2. ggf. neue Pricing-Variant in PRICING_VARIANTS oben.
 *   3. ggf. neue SlotId oben + Frontend-`<OfferSlot>` an der UI-Stelle.
 *   4. ggf. neue i18n-Keys (`offer.<id>.<slot>.<...>`) im Live-Frontend.
 *   5. ggf. Stripe-Coupon in Stripe-Console anlegen.
 *   6. Sandbox unter `/sandbox/?offer=<id>` durchklicken zum visuellen Check.
 *   7. Commit + Deploy.
 */
/**
 * Match-Key des Reveal-Modell-Offers (LEA-513). Als Konstante exportiert, damit
 * niemand `'reveal1'` als String-Literal durch den Code streut — die Prüfung
 * "läuft dieser User im neuen Abrechnungsmodell?" gehört an genau eine Stelle
 * (apps/api/src/lib/offerService.ts → isRevealModelUser).
 */
export const REVEAL_OFFER_ID = 'reveal1';

export const OFFERS: Record<string, OfferConfig> = {
  // Beta-Test-Einladung — User wird per persönlicher Einladung gebracht,
  // bekommt nur den Growth-Plan mit 55% Rabatt auf den ERSTEN Monat
  // (490€ → 220€ im 1. Monat, danach 490€). ACHTUNG (21.07.): der Live-
  // Coupon BETA55 hat duration=once — die frühere „dauerhaft"-Copy war
  // falsch und wurde auf Erstmonats-Framing korrigiert (og.beta.* Keys).
  // Visual-Spec: apps/web/public/prototype/sandbox/offers/beta/pricing.html
  beta: {
    id: 'beta',
    active: true,
    description:
      'Beta-Tester (Janik-Einladungen). Keine Test-Credits — User muss aktivieren um zu nutzen. Im Pricing nur Growth-Plan zur Auswahl mit 55% Rabatt auf den 1. Monat via Stripe-Coupon BETA55 (duration=once).',
    signup: {
      initialCredits: 0,
    },
    pricing: {
      variant: 'beta-only-growth',
      discountPercent: 55,
      // TODO Janik: Coupon `BETA55` muss in Stripe-Console angelegt sein
      // (55% off, applicable to Growth-Plan-Price).
      stripeCouponId: 'BETA55',
      skip: false,
    },
    slots: {
      'pricing.intro': {
        kind: 'text',
        textKey: 'offer.beta.pricing.intro',
      },
    },
  },
  // LEA-241 (2026-05-05): "50-Credits-Trial"-Kampagne.
  // Match-Source: ?source=50credits-trial / ls_offer=50credits-trial.
  // Beim Sign-up: 50 Initial-Credits + 3-Tage-Trial. Pricing-Modal
  // rendert die "50credits-trial"-Variante mit prominenter Trial-Box.
  // Slots minimal — kein Intro-Banner, da Trial-Box selbst den Punch trägt.
  '50credits-trial': {
    id: '50credits-trial',
    active: true,
    description:
      '50-Credits-Trial-Kampagne. 3 Tage kostenlos testen mit 50 Test-Credits, alle Plans wählbar. Pricing-Modal hebt den Trial-Block prominent hervor (i18n-Keys pricing.trial50.*).',
    signup: {
      initialCredits: 50,
      trialDaysOverride: 3,
    },
    pricing: {
      // LEA-319 (2026-06-11): Trust-Redesign-Variante. Rollback = wieder
      // '50credits-trial' eintragen (die alte Split-Testimonials-Variante
      // bleibt im PRICING_VARIANTS-Catalog erhalten → sofort reversibel,
      // ohne Asset-/i18n-Redeploy).
      variant: '50credits-trust',
      skip: false,
    },
    slots: {},
  },
  // LEA-261 (Janik 2026-05-11): "Mai-Aktion 2026". Match-Source:
  // ?source=mai-offer / ls_offer=mai-offer. Bezahlter Single-Card-Promo:
  // Basic-Plan, erster Monat 49 € (statt 190 €) via Stripe-Coupon MAI74
  // (one-cycle, in Stripe-Console von Janik angelegt am 2026-05-11).
  // Kein Trial, keine Initial-Credits — der User bezahlt direkt via
  // Stripe Checkout mit angewendetem Coupon. Evergreen: keine Expiry-
  // Logik hier; das May-Scarcity-Framing lebt auf der Landingpage.
  'mai-offer': {
    id: 'mai-offer',
    active: true,
    description:
      'Mai-Aktion 2026 (paid promo). Basic-Plan, erster Monat 49 € statt 190 € via Stripe-Coupon MAI74. Kein Trial — direkter Checkout mit Coupon. Evergreen, keine Expiry im Code.',
    signup: {
      initialCredits: 0,
    },
    pricing: {
      variant: 'mai-offer',
      // NOTE: `discountPercent` bewusst nicht gesetzt. MAI74 ist ein
      // one-cycle Stripe-Coupon (nur erster Monat). Würde `discountPercent`
      // gesetzt sein, würde PricingModal post-purchase die crossed-out 190 €
      // → 49 €-Anzeige unbegrenzt rendern (vergleiche BETA55 = permanent,
      // wo das korrekt ist). Die 49 €/190 €-Darstellung im Paywall-Hero
      // lebt stattdessen in den i18n-Keys og.maiOffer.price.* — nur das
      // Sign-up-Forced-Gate zeigt sie.
      //
      // LEA-261 follow-up (2026-05-12): `Mbr6domF` ist die Stripe-Coupon-
      // ID (nicht der human-readable Promotion-Code "MAI74"). Stripe's
      // Checkout-Session API erwartet `discounts: [{ coupon: '<id>' }]`,
      // nicht den Display-Label-String. "MAI74" zu übergeben würde mit
      // "No such coupon" failen (im Live-Test als 504 sichtbar geworden,
      // weil der SDK-Wrapper retried bis zum LB-Timeout).
      stripeCouponId: 'Mbr6domF',
      skip: false,
    },
    slots: {},
  },
  // LEA-241 Janik 2026-05-06 (Loom video #2): "Direct" link signup.
  // Match-Source: ?source=direct / ls_offer=direct.
  // Janik's intent: "no credit will be assigned, it will be normal
  // signup, it should not show the paywall page". Translated:
  //   - 0 initial credits (no promo gift)
  //   - default trial dates (maybeGrantTrial → accountStatus='trial',
  //     3-day cycle), but no forced gate
  //   - if the user later opens the in-app PricingModal, they see the
  //     standard 3-plan view (variant: 'standard') and can self-serve
  // Adding here (instead of treating "direct" as no-offer) so we can
  // log + audit "direct" attribution in offerEventRepository, and so
  // the OnboardingGate has a deterministic skip path (offer.pricing.skip).
  direct: {
    id: 'direct',
    active: true,
    description:
      'Direct link signup. No promo, no initial credits, no forced paywall — user lands in the product on a default trial and can subscribe via the in-app pricing modal when ready.',
    signup: {
      initialCredits: 0,
    },
    pricing: {
      variant: 'standard',
      skip: true,
    },
    slots: {},
  },
  // direct2 — LEA-453 (Janik, 22.07.2026): No-Card-Reverse-Trial. Journey:
  // ?offer=direct2 → Signup → 50 Credits liegen SOFORT im Wallet (gebucht als
  // creditEvent, siehe apps/api/src/services/offerSignupGrant.ts) → Dashboard
  // ohne erzwungene Paywall → das Pricing-Modal meldet sich erst von selbst,
  // wenn das Wallet auf 0 fällt. Die Paywall wandert also HINTER den
  // Aha-Moment statt davor.
  //
  // Warum die Felder so gesetzt sind:
  //   - initialCredits: 0 → das ist der STRIPE-Trial-Grant. Ein Wert > 0 würde
  //     bei der Conversion ein zweites Mal 50 Credits ausschütten
  //     (stripeWebhookHandler.ts:1764) und die Trial-Copy im Modal einschalten.
  //     Der Signup-Grant läuft ausschliesslich über walletCredits.
  //   - localTrial: false → keine Trial-Uhr. Die Credits verfallen nicht
  //     (Janik-Entscheidung 2), also darf weder expireTrial() den User auf
  //     'expired' kippen noch eine "Testphase endet"-Mail rausgehen.
  //   - variant: 'standard' → alle 3 Pläne, keine Offer-Sonderkarte.
  //     ACHTUNG: `variant` steuert NUR showPlans/headlineKey. Die Trial-Copy
  //     ("Die ersten 3 Tage sind kostenlos" / "3 Tage kostenlos starten")
  //     hängt in PricingModal an accountStatus === 'trial', und der bleibt
  //     hier wegen localTrial:false dauerhaft 'trial'. Unterdrückt wird sie
  //     deshalb dort über signup.localTrial, nicht über die Variant.
  //   - skip: true → kein erzwungener Gate (App.jsx:1053, OnboardingGate:377).
  //   - KEIN trialDaysOverride — sonst hängt billing.ts:581 einen Stripe-Trial
  //     an den Checkout und die Conversion landet im engen Promotion-Branch.
  direct2: {
    id: 'direct2',
    active: true,
    description:
      'No-card reverse trial (LEA-453). 50 credits in the wallet at signup, no card, no forced paywall — user lands in the product and can test immediately. The standard 3-plan pricing modal opens by itself once the wallet hits zero. Credits do not expire.',
    signup: {
      initialCredits: 0,
      walletCredits: 50,
      localTrial: false,
    },
    pricing: {
      variant: 'standard',
      skip: true,
      openOnZeroCredits: true,
    },
    slots: {},
  },
  // Prism (2026-07-01): Post-Signup-Aktivierungs-Funnel. Match-Source:
  // ?source=prism / ?offer=prism / ls_offer=prism (Landing setzt den Param,
  // Register mappt ihn auf attribution.source). Signup: KEINE Initial-Credits
  // (die 50 Leads kommen erst aus dem 49-€-Tripwire-Kauf); Default-Trial-Dates
  // (accountStatus='trial') damit der Forced-Gate erscheint. Der Gate rendert
  // bei variant/layout 'prism' den PrismShell-Funnel (features/prism/) statt
  // der Plan-Karten. Angebot dahinter (Stufe 2): 49 € einmalig → 50 Credits
  // sofort + 3 Tage → ab Tag 4 Starter-Abo (250 Credits / 290 €). Additiv —
  // bestehende Offers unberührt.
  prism: {
    id: 'prism',
    active: true,
    description:
      'Prism-Aktivierungs-Funnel (post-signup). Zielgruppe → Website-KI-Analyse → 3 Fragen → echte Suche → Aha-Ergebnis → Paywall → Inline-Checkout. 49-€-Tripwire (50 Leads + 3 Tage) → ab Tag 4 Starter-Abo. Gerendert vom features/prism-Modul.',
    signup: {
      initialCredits: 0,
    },
    pricing: {
      variant: 'prism',
      skip: false,
    },
    slots: {},
  },
  // reveal1 (LEA-513, 2026-08-11) — der Schalter für das neue Self-Service-
  // Modell. Ein einzelner User trägt ihn, alle anderen behalten das heutige
  // Verhalten; jedes Durchsickern in Bestandskunden ist ein P0 (LEA-518).
  //
  // Warum jedes Feld so steht:
  //   - initialCredits: 0 UND kein walletCredits → es wird gar nichts
  //     verschenkt. Suchen und Kontakt-Recherche sind in diesem Modell frei,
  //     also braucht der User kein Startguthaben, um anzufangen. Guthaben
  //     bedeutet hier ausschliesslich "Kontakte, die ich freischalten kann".
  //   - localTrial: false → keine Trial-Uhr. LOAD-BEARING: mit Uhr kippt
  //     expireTrial() den accountStatus irgendwann auf 'expired', und der
  //     LEA-475-Gate in preflightPhaseStart antwortet dann mit 402 — genau
  //     die Wand, die §1 der Vision im Gratis-Pfad verbietet. Unterdrückt
  //     zusätzlich die kalender-basierten Trial-Mails, deren Copy hier falsch
  //     wäre (es endet keine Testphase).
  //   - skip: true → kein erzwungener Gate beim Signup. Der User landet direkt
  //     im Produkt und darf analysieren, suchen und recherchieren, bevor je
  //     von Geld die Rede war.
  //   - openOnZeroCredits: FALSE — bewusst anders als direct2. Dort ist ein
  //     leeres Wallet der Ausnahmezustand am Ende des Trials; hier ist es der
  //     NORMALZUSTAND (jeder neue Account startet auf 0 und darf trotzdem
  //     alles Freie tun). Ein Modal, das sich bei jedem Nulldurchgang selbst
  //     öffnet, wäre exakt die Wand aus §1. Die Pricing-Tabelle geht stattdessen
  //     an dem einen ehrlichen Moment auf: 402 `reveal_requires_contacts`, wenn
  //     der User einen konkreten, erreichbaren Entscheider freischalten will.
  //   - variant: 'standard' → die neue Pricing-Tabelle (LEA-516) ist noch nicht
  //     gebaut; bis dahin keine eigene Variant erfinden, die niemand rendert.
  //   - active: true — auf production seit 16.10.2026 (LEA-518-Launch). Vorher
  //     stand es hier auf false: ohne die serverseitige Maskierung (LEA-514)
  //     wäre Connect gratis UND unmaskiert gewesen, ohne Fair-Use (LEA-512)
  //     ungedeckelt, ohne die UI (#911/#941) eine Sackgasse beim Bezahlen.
  //     Alle drei sind da. Janik 15.10.: reveal1 ist ein Angebot, nicht der
  //     Default — nur wer über ?offer=reveal1 kommt, landet im Modell;
  //     production hat keinen Default-Offer-Fallback. Zurück auf false schaltet
  //     das Modell komplett ab (isRevealModelUser liest dieses Flag, auch der
  //     Env-Weg).
  [REVEAL_OFFER_ID]: {
    id: REVEAL_OFFER_ID,
    active: true,
    description:
      'Reveal-Modell (LEA-513): Suchen und Kontakt-Recherche kostenlos, bezahlt wird nur das Freischalten eines verifizierten, erreichbaren Entscheiders — Akquise-Entwurf inklusive. Kein Startguthaben, keine Trial-Uhr, kein erzwungener Gate.',
    signup: {
      initialCredits: 0,
      localTrial: false,
    },
    pricing: {
      variant: 'standard',
      skip: true,
      openOnZeroCredits: false,
    },
    slots: {},
  },
};

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Resolved den OfferConfig anhand `attribution.source`. Case-insensitive.
 *
 * - null/leer → null (= Standard-Verhalten).
 * - Unbekannte ID → null (Audit-Log das via Sign-up-Handler).
 * - Inaktiver Offer → null (gleichbedeutend mit unbekannt).
 *
 * Reine Funktion, kein DB-Zugriff. Sicher überall verwendbar.
 */
export function resolveOffer(source: string | null | undefined): OfferConfig | null {
  if (!source) return null;
  const normalized = source.trim().toLowerCase();
  if (!normalized) return null;
  const cfg = OFFERS[normalized];
  if (!cfg) return null;
  if (!cfg.active) return null;
  return cfg;
}

/**
 * Liefert den Default-`OfferConfig` (= kein Offer aktiv) als explizit-typed
 * "no-op". Convenience für Frontend-Stellen die immer einen Wert wollen.
 */
export const NO_OFFER: null = null;

/**
 * LEA-513 — läuft dieser User im Reveal-Abrechnungsmodell?
 *
 * Steht hier in @cortex/schemas und nicht in apps/api, weil die Frage an ZWEI
 * Stellen in ZWEI Paketen beantwortet werden muss: an den HTTP-Gates
 * (apps/api/src/routes/scraper.ts) und im Orchestrator selbst
 * (services/scraper), der einen Lauf bei leerem Guthaben auf
 * 'paused_out_of_credits' setzt. Genau diese zweite Stelle war der QA-Fund vom
 * 12.08.: die HTTP-Wand war weg, der Lauf pausierte sich eine Ebene tiefer
 * trotzdem selbst — für den Kunden derselbe kaputte Ablauf. Eine zweite Kopie
 * der Bedingung im Scraper hätte genau dieselbe Drift nochmal eingebaut, also
 * gibt es die Definition ab jetzt einmal, dort wo beide Pakete sie sehen.
 *
 * Drei Wege ins Modell, alle bewusst eng:
 *
 *   1. Das Offer `reveal1` am User — der reguläre Weg.
 *   2. `NEW_MODEL_BILLING_UX=1` (Env) als QA-Schalter, damit das Modell ohne
 *      offer-attribuierten Account durchspielbar ist. Er greift ausschliesslich
 *      bei Usern OHNE eigenes aktives Offer: ein Kunde mit
 *      direct2/mono99/prism/prism2 behält sein Verhalten, selbst wenn jemand
 *      das Env versehentlich auf PROD setzt. Grandfathering ist ein P0 und darf
 *      nicht davon abhängen, dass niemand einen Schalter falsch umlegt.
 *   3. LEA-704 (Janik 15.10.): `billingModel: 'reveal'` am Konto — die
 *      Migration abgelaufener Bestandskonten. Ein eigenes Feld statt
 *      `attribution.source` umzuschreiben, weil die Attribution die Herkunft
 *      des Kunden ist und die offer-basierte Trial-Logik daran hängt. Gesetzt
 *      erst mit dem Klick auf dem Willkommen-zurück-Screen. Ein aktives Abo
 *      gewinnt immer: wer wieder abonniert (`accountStatus: 'subscribed'`), ist
 *      zurück im Credit-Modell seines Plans — ohne dass jemand das Feld löscht.
 *
 * `resolvedOfferId` ist der Ausweg aus einer sonst unvermeidlichen Lücke:
 * apps/api löst das Offer über resolveOfferFromUser auf, das zusätzlich den
 * DEFAULT_SIGNUP_OFFER_ID-Fallback kennt (ein Rollout-Weg, der hier bewusst
 * nicht nachgebaut wird). Wer diesen Wert hat, reicht ihn durch; wer ihn nicht
 * hat — der Orchestrator — bekommt trotzdem für beide echten Schalter oben die
 * richtige Antwort.
 *
 * `envOverrideOn` wird HEREINGEREICHT statt hier gelesen: dieses Paket läuft
 * auch im Browser (apps/web importiert @cortex/schemas), und `process.env` gibt
 * es dort nicht. Wichtig ist ohnehin nicht, WER die Variable liest — das ist ein
 * konstanter String-Vergleich — sondern dass die REGEL dahinter (der Override
 * gilt nur für nicht-attribuierte User) genau einmal existiert. Genau die ist
 * grandfathering-kritisch, und genau die steht hier.
 */
export function isRevealModelUser(
  user:
    | {
        attribution?: { source?: string | null } | null;
        billingModel?: string | null;
        accountStatus?: string | null;
      }
    | null
    | undefined,
  opts?: { resolvedOfferId?: string | null; envOverrideOn?: boolean },
): boolean {
  if (!user) return false;
  // Ein deaktiviertes reveal1 schaltet das Modell komplett ab — auch den
  // NEW_MODEL_BILLING_UX-Weg, der sonst jeden nicht-attribuierten Altkunden
  // hineinstellen würde, falls die Variable auf production gesetzt ist.
  if (!OFFERS[REVEAL_OFFER_ID]?.active) return false;
  if (user.billingModel === 'reveal' && user.accountStatus !== 'subscribed') return true;
  if (opts?.resolvedOfferId === REVEAL_OFFER_ID) return true;
  const attributed = resolveOffer(user.attribution?.source);
  if (attributed?.id === REVEAL_OFFER_ID) return true;
  if (!opts?.envOverrideOn) return false;
  // Env-Override nur für nicht-attribuierte User — siehe Punkt 2 oben.
  return attributed === null;
}
